import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import { Mic, BarChart2 } from "lucide-react";

// Adjust these imports based on your project's directory structure
import useApiEp from "../hooks/useApiEp";
import { getErrorResponse } from "../utils/util";
import DataTable from "../widgets/DataTable";
import RenderStatusMessage from "../widgets/RenderStatusMessage";
import CampaignDetailsModal from "./VoiceCampaignDetail";
import { FaWhatsapp } from "react-icons/fa";
import { MessagesSquare } from "lucide-react";

/* ── extraction status chip ──────────────────────────────── */
const ExtractionStatusChip = ({ status }) => {
  const map = {
    completed: { label: "Extracted", color: "success" },
    in_progress: { label: "In Progress", color: "warning" },
    failed_extraction: { label: "Failed", color: "error" },
  };
  const cfg = map[status] ?? { label: status ?? "—", color: "default" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      color={cfg.color}
      variant="outlined"
      sx={{ height: 20, fontSize: 10 }}
    />
  );
};

/* ── single voice campaign row ───────────────────────────── */
const VoiceCampaignRow = ({ campaign, idx, page, limit }) => {
  const serial = (page - 1) * limit + idx + 1;
  const lastRetry = campaign.extractRetries?.at(-1) ?? null;

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString("en-IN", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "—";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 1.75,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.15s",
        flexWrap: "wrap",
        "&:hover": { boxShadow: "0 3px 8px rgba(0,0,0,0.08)" },
      }}
    >
      {/* Serial */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ minWidth: 24, fontVariantNumeric: "tabular-nums" }}
      >
        {serial}
      </Typography>

      {/* Icon */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: "success.50",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Mic size={15} strokeWidth={1.8} style={{ color: "#2e7d32" }} />
      </Box>

      {/* Name + Campaign ID */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={700}
          noWrap
          title={campaign.campaignName}
        >
          {campaign.campaignName ?? "—"}
        </Typography>
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontFamily: "monospace" }}
        >
          ID: {campaign.campaignId ?? "—"}
        </Typography>
      </Box>

      {/* Extraction status */}
      <ExtractionStatusChip status={campaign.extractionStatus} />

      {/* Retry count */}
      {campaign.extractRetries?.length > 0 && (
        <Tooltip
          arrow
          title={`Last attempt: ${lastRetry?.status} · ${formatDate(
            lastRetry?.ts
          )}`}
        >
          <Chip
            label={`${campaign.extractRetries.length} ${
              campaign.extractRetries.length === 1 ? "retry" : "retries"
            }`}
            size="small"
            variant="outlined"
            color="default"
            sx={{ height: 20, fontSize: 10, cursor: "default" }}
          />
        </Tooltip>
      )}

      {/* Created date */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ whiteSpace: "nowrap" }}
      >
        {formatDate(campaign.createdAt)}
      </Typography>
    </Box>
  );
};

/* ── voice report tab ────────────────────────────────────── */
export const VoiceReportTab = ({ groupId }) => {
  const [page, setPage] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const limit = 30;

  const { data, isLoading, isError, error } = useApiEp(
    `/voice/get-group-campaigns/${groupId}?page=${page}&limit=${limit}`
  );

  const rows = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 1;

  if (isError)
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {getErrorResponse(error)}
      </Alert>
    );

  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );

  if (rows.length === 0)
    return (
      <Box
        sx={{
          py: 4,
          border: "1.5px dashed",
          borderColor: "divider",
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.disabled">
          No voice campaigns found for this group.
        </Typography>
      </Box>
    );

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1.5, display: "block" }}
      >
        {total} campaign{total !== 1 ? "s" : ""} total
      </Typography>

      <Stack spacing={1}>
        {rows.map((campaign, idx) => (
          <Box
            key={campaign._id ?? idx}
            onClick={() => setSelectedCampaign(campaign)}
            sx={{
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 2,
                borderRadius: 1,
              },
            }}
          >
            <VoiceCampaignRow
              campaign={campaign}
              idx={idx}
              page={page}
              limit={limit}
            />
          </Box>
        ))}
      </Stack>

      {totalPages > 1 && (
        <Box
          sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2.5 }}
        >
          <Button
            size="small"
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
          >
            Previous
          </Button>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ alignSelf: "center", px: 1 }}
          >
            {page} / {totalPages}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Detail Modal */}
      <CampaignDetailsModal
        open={Boolean(selectedCampaign)}
        onClose={() => setSelectedCampaign(null)}
        campaign={selectedCampaign}
      />
    </Box>
  );
};

/* ── report table for one channel (WhatsApp/RCS) ────────── */
const CampaignReportTab = ({ groupId, type }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 30;

  const { data, isLoading, isError, error } = useApiEp(
    `/campaign/get-campaign-report-list/${groupId}/${type}?page=${page}&limit=${limit}`
  );

  const handleAction = useCallback(
    (action, rowId) => {
      if (action === "view") {
        navigate(`/home/${rowId}/message-detail?isRcs=${type === "rcs"}`);
      }
    },
    [navigate, type]
  );

  if (isError)
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {getErrorResponse(error)}
      </Alert>
    );
  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <DataTable
      columns={[
        {
          id: "serial",
          label: "S.No.",
          render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
        },
        { id: "campaignName", label: "Campaign" },
        { id: "to", label: "Recipient" },
        {
          id: "status",
          label: "Status",
          render: (v) => <RenderStatusMessage status={v} />,
        },
        {
          id: "createdOn",
          label: "Created On",
          render: (v) =>
            new Date(v).toLocaleString("en-IN", { dateStyle: "medium" }),
        },
      ]}
      actions={["view"]}
      rows={data?.data ?? []}
      page={page}
      limit={limit}
      totalPages={data?.totalPages ?? 1}
      onPageChange={(p) => setPage(p)}
      onAction={handleAction}
    />
  );
};

/* ── tabbed report section (Main Component) ──────────────── */
const CampaignReportTabs = ({ groupId }) => {
  const [activeTab, setActiveTab] = useState(0);

  const { data: wabaCountData } = useApiEp(
    `/campaign/get-campaign-report-count/${groupId}/whatsapp`
  );
  const { data: rcsCountData } = useApiEp(
    `/campaign/get-campaign-report-count/${groupId}/rcs`
  );

  const wabaCount = wabaCountData?.count ?? 0;
  const rcsCount = rcsCountData?.count ?? 0;

  return (
    <Box
      sx={{
        mt: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      {/* Section heading */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <BarChart2 size={16} strokeWidth={1.8} style={{ color: "#666" }} />
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          Message Reports
        </Typography>
      </Box>

      <Divider />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
        >
          {/* WhatsApp tab */}
          <Tab
            disableRipple
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaWhatsapp size={15} strokeWidth={1.8} />
                <span>WhatsApp</span>
                <Chip
                  label={wabaCount}
                  size="small"
                  color="primary"
                  sx={{ height: 18, fontSize: 10, pointerEvents: "none" }}
                />
              </Box>
            }
          />

          {/* RCS tab */}
          <Tab
            disableRipple
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MessagesSquare size={15} strokeWidth={1.8} />
                <span>RCS</span>
                <Chip
                  label={rcsCount}
                  size="small"
                  color="secondary"
                  sx={{ height: 18, fontSize: 10, pointerEvents: "none" }}
                />
              </Box>
            }
          />

          {/* Voice tab */}
          <Tab
            disableRipple
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Mic size={15} strokeWidth={1.8} />
                <span>Voice</span>
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab panels */}
      <Box sx={{ p: 2 }}>
        {activeTab === 0 && (
          <CampaignReportTab groupId={groupId} type="whatsapp" />
        )}
        {activeTab === 1 && <CampaignReportTab groupId={groupId} type="rcs" />}
        {activeTab === 2 && <VoiceReportTab groupId={groupId} />}
      </Box>
    </Box>
  );
};

export default CampaignReportTabs;
